/**
 * Hooks for CameraPreview: shutter flash, track constraints, focus, pinch zoom.
 */

import { useRef, useState, useEffect, useCallback } from "react";
import {
  SHUTTER_VISIBLE_MS,
  FOCUS_INDICATOR_DURATION_MS,
  PINCH_ZOOM_SENSITIVITY,
  PINCH_ZOOM_THRESHOLD,
} from "../constants";
import { getTouchDistance } from "../utils";
import {
  getVideoTrack,
  isTorchSupported,
  getZoomRangeFromTrack,
  setTorch,
  setZoom,
  stopStream,
} from "../services/mediaTrackManager";

export function useShutterFlash(visibleMs = SHUTTER_VISIBLE_MS) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  const trigger = useCallback(() => {
    setVisible(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setVisible(false);
      timerRef.current = null;
    }, visibleMs);
  }, [visibleMs]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { shutterVisible: visible, triggerShutterFlash: trigger };
}

/**
 * Manages video track/stream: applies torch/zoom, reports capabilities, stops tracks on unmount, listens for device change.
 * Uses mediaTrackManager for all track operations (testable).
 */
export function useCameraTrack(
  trackRef,
  streamRef,
  { flashOn, zoomLevel, onTorchSupportChange, onZoomSupportChange, onDeviceChange }
) {
  const flashOnRef = useRef(flashOn);
  flashOnRef.current = flashOn;

  const onUserMedia = useCallback(
    (stream) => {
      const prev = streamRef.current;
      if (prev && prev !== stream) {
        stopStream(prev);
      }
      const track = getVideoTrack(stream);
      streamRef.current = stream;
      trackRef.current = track;
      if (!track) return;
      try {
        const torchSupported = isTorchSupported(track);
        onTorchSupportChange?.(torchSupported);
        if (torchSupported && flashOnRef.current) {
          setTorch(track, true);
        }
        const zoomRange = getZoomRangeFromTrack(track);
        onZoomSupportChange?.(zoomRange ?? null);
        if (zoomRange) {
          setZoom(track, zoomLevel);
        }
      } catch {
        onTorchSupportChange?.(false);
        onZoomSupportChange?.(null);
      }
    },
    [trackRef, streamRef, onTorchSupportChange, onZoomSupportChange, zoomLevel]
  );

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    setTorch(track, flashOn);
  }, [trackRef, flashOn]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    setZoom(track, zoomLevel);
  }, [trackRef, zoomLevel]);

  useEffect(() => {
    const mediaDevices = typeof navigator !== "undefined" ? navigator.mediaDevices : null;
    if (!mediaDevices?.addEventListener || !onDeviceChange) return;
    const handler = () => onDeviceChange();
    mediaDevices.addEventListener("devicechange", handler);
    return () => mediaDevices.removeEventListener("devicechange", handler);
  }, [onDeviceChange]);

  useEffect(() => {
    return () => {
      const track = trackRef.current;
      const stream = streamRef.current;
      if (track) setTorch(track, false);
      stopStream(stream);
      streamRef.current = null;
      trackRef.current = null;
    };
  }, [trackRef, streamRef]);

  return { onUserMedia };
}

export function useFocusIndicator(containerRef, durationMs = FOCUS_INDICATOR_DURATION_MS) {
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [active, setActive] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleTap = useCallback(
    (e) => {
      e.stopPropagation();
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      if (timerRef.current) clearTimeout(timerRef.current);
      setPosition({ x, y });
      setActive(true);
      timerRef.current = setTimeout(() => {
        setActive(false);
        timerRef.current = null;
      }, durationMs);
    },
    [containerRef, durationMs]
  );

  return { focusPosition: position, focusActive: active, handleFocusTap: handleTap };
}

export function usePinchZoom(onZoomDelta) {
  const pinchStartRef = useRef(null);

  const handleTouchStart = useCallback(
    (e) => {
      if (e.touches.length === 2 && onZoomDelta) {
        pinchStartRef.current = getTouchDistance(e.touches);
      } else {
        pinchStartRef.current = null;
      }
    },
    [onZoomDelta]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (e.touches.length !== 2 || !onZoomDelta || pinchStartRef.current == null) return;
      e.preventDefault();
      const dist = getTouchDistance(e.touches);
      const start = pinchStartRef.current;
      if (start <= 0) return;
      const ratio = dist / start;
      const delta = (ratio - 1) * PINCH_ZOOM_SENSITIVITY;
      if (Math.abs(delta) >= PINCH_ZOOM_THRESHOLD) {
        onZoomDelta(delta);
        pinchStartRef.current = dist;
      }
    },
    [onZoomDelta]
  );

  const handleTouchEnd = useCallback(() => {
    pinchStartRef.current = null;
  }, []);

  return { handleTouchStart, handleTouchMove, handleTouchEnd };
}

/**
 * Applies focus point to ImageCapture when focusPosition or cam ref changes.
 */
export function useImageCaptureFocus(camRef, focusPosition) {
  useEffect(() => {
    const node = camRef.current;
    const videoEl = node?.video ?? node;
    if (!videoEl?.srcObject?.getVideoTracks) return;
    const track = videoEl.srcObject.getVideoTracks()[0];
    if (!track) return;
    const ImageCaptureCtor = window.ImageCapture;
    if (!ImageCaptureCtor) return;
    try {
      const ic = new ImageCaptureCtor(track);
      if (typeof ic.setOptions !== "function") return;
      const x = focusPosition.x / 100;
      const y = focusPosition.y / 100;
      ic.setOptions({ pointsOfInterest: [{ x, y }] }).catch(() => {});
    } catch {
      // Not supported
    }
  }, [camRef, focusPosition.x, focusPosition.y]);
}

/**
 * Combines a forwarded ref with a local ref. Use for Webcam/custom components.
 */
export function useComposedRef(forwardedRef, localRef) {
  return useCallback(
    (el) => {
      localRef.current = el;
      if (typeof forwardedRef === "function") {
        forwardedRef(el);
      } else if (forwardedRef) {
        forwardedRef.current = el;
      }
    },
    [forwardedRef, localRef]
  );
}
